import * as React from "react";
import { graphql } from "gatsby";
import Seo from "../components/Seo";

import { solutionsPageTplData } from "../data/page-tpl-solutions.data";
import { getCurrentPageSeo } from "../helpers/richContentHelpers";

import Layout from "../components/Layout";
import PageFooterSections from "../components/PageFooterSections";
import Section from "../components/Section";

import Hero from "../componentsSections/Hero";
import AnchorNav from "../componentsSections/AnchorNav";
import KeyBenefits from "../componentsSections/KeyBenefits";
import SocialProof, {
    hasSocialProofContent,
} from "../componentsSections/SocialProof";
import Lottie from "../componentsSections/Lottie";
import AlternatingFeatures from "../componentsSections/AlternatingFeatures";
import SecurityWithVisual from "../componentsSections/SecurityWithVisual";
import ClientCases from "../componentsSections/ClientCases";
import ImageSectionsGroup from "../components/ImageSectionsGroup";
import ImageSection from "../components/ImageSection";

const SolutionsPage = ({ pageContext: { id } }) => {
    const seoPageData = getCurrentPageSeo(id);
    const pageData = solutionsPageTplData(id);

    const keyFeaturesSections = pageData.keyFeatures?.features
        ? pageData.keyFeatures.features.map((feature) => ({
              header: feature.title,
              image: feature.image,
              imagePosition: feature.imagePosition,
              features: [
                  {
                      title: feature.title,
                      description: feature.description,
                      link: feature.link,
                  },
              ],
          }))
        : [];

    const showSocialProof = hasSocialProofContent({
        trustedByLogos: pageData.socialProof?.trustedByLogos,
        testimonials: pageData.socialProof?.testimonials,
        stats: pageData.socialProof?.stats,
    });

    const keyFeatures = pageData.keyFeatures || [];
    const hasOperatingModel = Boolean(
        pageData.operatingModel?.header ||
            pageData.operatingModel?.lottieSrc?.localFile?.publicURL
    );

    const socialProofSection = showSocialProof && (
        <Section className="-mobile-full">
            <SocialProof
                trustedByHeader={pageData.socialProof?.header}
                trustedByLogos={pageData.socialProof?.trustedByLogos}
                testimonials={pageData.socialProof?.testimonials}
                stats={pageData.socialProof?.stats}
                statsHeader={pageData.socialProof?.statsHeader}
                mode="full"
            />
        </Section>
    );

    return (
        <Layout
            pageTitle={seoPageData.title}
            navbarVariant={pageData.hero?.image ? "transparent" : undefined}
        >
            <Seo post={seoPageData} />

            <Hero {...pageData.hero} />

            <Section className="-mobile-full">
                <AnchorNav items={pageData.anchorNav} />
            </Section>

            {!hasOperatingModel && pageData.benefits?.items?.length > 0 && (
                <Section>
                    <KeyBenefits
                        {...pageData.benefits}
                        benefits={pageData.benefits.items}
                    />
                </Section>
            )}

            {hasOperatingModel ? (
                <>
                    <Section className="-mobile-full">
                        <ImageSectionsGroup
                            imageSections={keyFeatures.slice(0, 1)}
                        />
                    </Section>

                    <Section className="-mobile-full">
                        <Lottie {...pageData.operatingModel} variant="light" />
                    </Section>

                    {socialProofSection}

                    {keyFeatures.length > 1 && (
                        <Section className="-mobile-full">
                            <ImageSectionsGroup
                                imageSections={keyFeatures.slice(1)}
                                indexOffset={1}
                            />
                        </Section>
                    )}
                </>
            ) : (
                <>
                    {socialProofSection}

                    <Section className="-mobile-full">
                        <ImageSectionsGroup imageSections={keyFeatures} />
                    </Section>
                </>
            )}

            {pageData.imageSection?.variant && (
                <Section className="-mobile-full">
                    <ImageSection imageSection={pageData.imageSection} />
                </Section>
            )}

            <Section>
                <ClientCases
                    header={pageData.industryCases?.header}
                    slug={pageData.industryCases?.slug}
                    cases={pageData.industryCases?.cases?.map((c) => ({
                        category: c.title,
                        ...c,
                    }))}
                />
            </Section>

            <PageFooterSections pageData={pageData} />
        </Layout>
    );
};

export default SolutionsPage;
